import { useMemo, useCallback, memo } from 'react'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { CategoriesBaseProps, CategoryItem, defaultCategories, categoryColors, formatChange } from './CategoriesBase'
import { ProjectLogo } from './ProjectLogo'

const CategoryItemComponent = memo(({ 
  item, 
  index, 
  onItemClick 
}: { 
  item: CategoryItem
  index: number
  onItemClick?: (item: CategoryItem) => void 
}) => {
  const change = item.change || 0
  const color = categoryColors[index % categoryColors.length]
  
  const handleClick = useCallback(() => {
    onItemClick?.(item)
  }, [item, onItemClick])
  
  const changeText = formatChange(change)
  
  return (
    <div
      className="bg-white dark:bg-[#1c1c1c] flex items-center justify-between pl-3 pr-[18px] py-3 cursor-pointer hover:opacity-90 transition-opacity"
      onClick={handleClick}
    >
      <div className="flex gap-[10px] items-start">
        <ProjectLogo 
          logo={item.logo} 
          alt={item.project_name || 'Project'}
          color={color}
        />
        <div className="flex items-baseline">
          <p 
            className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base overflow-hidden text-ellipsis whitespace-nowrap"
            
          >
            {item.project_name || 'Project'}
          </p>
        </div>
      </div>
      <div className="flex flex-col justify-center overflow-hidden text-ellipsis whitespace-nowrap">
        <p 
          className="font-plus-jakarta font-semibold leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] overflow-hidden text-ellipsis whitespace-nowrap"
          
        >
          {changeText}
        </p>
      </div>
    </div>
  )
})

CategoryItemComponent.displayName = 'CategoryItemComponent'

export const CategoriesMobile = ({ 
  items, 
  categories = defaultCategories,
  selectedCategory = '',
  onCategoryChange,
  onItemClick,
  loading = false
}: CategoriesBaseProps) => {
  const { scrollContainerRef, isDragging, handlers, style } = useHorizontalScroll()

  const filteredItems = useMemo(() => {
    return selectedCategory 
      ? items.filter(item => item.category === selectedCategory || item.industry === selectedCategory)
      : items
  }, [items, selectedCategory])

  const handleCategoryClick = useCallback((category: string) => {
    if (!isDragging) {
      onCategoryChange?.(category)
    }
  }, [isDragging, onCategoryChange])

  // Filter categories to only show those with orders
  const categoriesWithOrders = useMemo(() => {
    const allCategories = categories.length > 0 ? categories : defaultCategories
    return allCategories.filter(category => {
      return items.some(item => item.category === category || item.industry === category)
    })
  }, [categories, items])

  // Use first category as default if none selected or if selected category has no orders
  const displayCategories = categoriesWithOrders
  const activeCategory = (selectedCategory && displayCategories.includes(selectedCategory)) 
    ? selectedCategory 
    : displayCategories[0] || ''

  if (loading) {
    return (
      <div className="h-full flex flex-col relative">
        <p 
          className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase"
          
        >
          Categories
        </p>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading categories...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <p 
        className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase mb-3"
        
      >
        Categories
      </p>

      {/* Category tabs */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-5 items-center mb-3 overflow-x-auto scrollbar-hide -mx-1 px-1"
        {...handlers}
        style={style}
      >
        {displayCategories.map((category) => {
          const isSelected = category === activeCategory
          return (
            <button
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`font-plus-jakarta leading-[20px] text-sm pb-2 pt-1 whitespace-nowrap flex-shrink-0 transition-colors ${
                isSelected
                  ? 'font-semibold text-[#2d2923] dark:text-[#fdfdfd] border-b border-[#2d2923] dark:border-[#fdfdfd]'
                  : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] hover:text-[#2d2923] dark:hover:text-[#fdfdfd]'
              }`}
              
            >
              {category}
            </button>
          )
        })}
      </div>

      {/* Project list */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {filteredItems.map((item, index) => (
          <CategoryItemComponent
            key={item.order_id || index}
            item={item}
            index={index}
            onItemClick={onItemClick}
          />
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            No items in this category
          </div>
        )}
      </div>
    </div>
  )
}

